import type { Properties } from 'posthog-js'
import { buildPostHogOptions } from './config'
import { readPostHogConsent, subscribeToConsentChanges } from './consent'
import { activatePostHog, deactivatePostHog, getPostHog } from './client'
import { buildPostHogIdentity, PostHogIdentity, TrackingInformation } from './identify'
import { resolvePostHogSettings, PostHogSettings } from './settings'

/**
 * Entry point of the PostHog integration.
 *
 * Flow:
 *   startPostHogTracking()  – once at bootstrap; resolves settings and subscribes to Usercentrics
 *   consent given           – SDK chunk is loaded, initialised, pending identity is applied
 *   consent revoked         – SDK is opted out and reset immediately, no reload required
 *   identifyPostHogUser()   – after login / on bootstrap with the tracking information; queued
 *                             until consent exists
 *   resetPostHogUser()      – on logout / tenant switch
 */

/** How often the consent is re-read while the CMP has not answered yet (backoff: n seconds). */
const MAX_CONSENT_RETRIES = 5

let started = false
let settings: PostHogSettings | null = null
let pendingIdentity: PostHogIdentity | null = null
let consentRetries = 0
let unsubscribeFromConsent: (() => void) | null = null

const applyIdentity = (identity: PostHogIdentity) => {
  const client = getPostHog()
  if (!client) {
    return
  }
  try {
    client.identify(identity.distinctId, identity.properties)
  } catch (error) {
    console.error('Error identifying PostHog user:', error)
  }
}

const activate = async() => {
  if (!settings) {
    return
  }
  try {
    await activatePostHog(settings.token, buildPostHogOptions(settings.apiHost))
  } catch (error) {
    console.error('Error loading PostHog:', error)
    return
  }
  if (pendingIdentity) {
    applyIdentity(pendingIdentity)
  }
}

const scheduleConsentRetry = () => {
  if (consentRetries >= MAX_CONSENT_RETRIES) {
    return
  }
  consentRetries++
  window.setTimeout(applyConsent, 1000 * consentRetries)
}

const applyConsent = () => {
  readPostHogConsent()
    .then(consent => {
      if (consent === true) {
        return activate()
      }
      if (consent === false) {
        deactivatePostHog()
      } else {
        scheduleConsentRetry()
      }
    })
    .catch(error => {
      console.error('Error evaluating PostHog consent:', error)
    })
}

/**
 * Starts the integration. Returns a stop function that unsubscribes from the CMP and forgets the
 * state, so a later start evaluates everything again (used by tests, the app never stops).
 */
export const startPostHogTracking = (): (() => void) => {
  if (started) {
    return stopPostHogTracking
  }
  started = true
  settings = resolvePostHogSettings()
  if (!settings) {
    return stopPostHogTracking
  }

  if (!settings.requiresConsent) {
    activate()
    return stopPostHogTracking
  }

  // subscribe first, then read: a CMP that initialises in between is not missed
  unsubscribeFromConsent = subscribeToConsentChanges(applyConsent)
  applyConsent()
  return stopPostHogTracking
}

const stopPostHogTracking = () => {
  unsubscribeFromConsent?.()
  unsubscribeFromConsent = null
  started = false
  settings = null
  pendingIdentity = null
  consentRetries = 0
}

/** `true` while PostHog is configured for this build and waits for the Usercentrics consent. */
export const isPostHogConsentRequired = (): boolean => settings?.requiresConsent === true

/** Re-reads the Usercentrics consent, e.g. right after the app wrote a consent itself. */
export const refreshPostHogConsent = (): void => {
  if (settings?.requiresConsent) {
    applyConsent()
  }
}

export const identifyPostHogUser = (trackingData: TrackingInformation): void => {
  if (!settings) {
    return
  }
  pendingIdentity = buildPostHogIdentity(trackingData, settings.idPrefix)
  applyIdentity(pendingIdentity)
}

export const resetPostHogUser = (): void => {
  pendingIdentity = null
  getPostHog()?.reset()
}

export const capturePostHogEvent = (eventName: string, properties?: Properties): void => {
  getPostHog()?.capture(eventName, properties)
}

/** Sends a caught error as `$exception` (no-op without consent). */
export const capturePostHogException = (error: unknown, properties?: Properties): void => {
  getPostHog()?.captureException(error, properties)
}
